/**
 * Financial Summary Card Component
 *
 * Component for displaying financial summary metrics
 */

import React from 'react';
import {FinancialSummary} from '../models/report.model';
import {AppCardTitle, AppBodyText} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';

interface FinancialSummaryCardProps {
  summary: FinancialSummary & {
    total_orders?: number;
    average_order_value?: number;
  };
  showAdditionalMetrics?: boolean;
}

export const FinancialSummaryCard: React.FC<FinancialSummaryCardProps> = ({
  summary,
  showAdditionalMetrics = false
}) => {
  const metrics = [
    {
      label: 'إجمالي المتاجر',
      value: summary.total_stores,
      icon: '🏪',
      color: 'blue'
    },
    {
      label: 'إجمالي الفواتير',
      value: formatCurrency(summary.total_invoices),
      icon: '📊',
      color: 'green'
    },
    {
      label: 'إجمالي المدفوع',
      value: formatCurrency(summary.total_paid),
      icon: '✅',
      color: 'green'
    },
    {
      label: 'المتبقي',
      value: formatCurrency(summary.total_outstanding),
      icon: '⏳',
      color: 'yellow'
    },
    {
      label: 'معدل التحصيل',
      value: `${summary.collection_rate.toFixed(1)}%`,
      icon: '📈',
      color: 'blue'
    }
  ];

  if (showAdditionalMetrics && summary.total_orders && summary.average_order_value) {
    metrics.push(
      {
        label: 'إجمالي الطلبات',
        value: summary.total_orders,
        icon: '📦',
        color: 'purple'
      },
      {
        label: 'متوسط قيمة الطلب',
        value: formatCurrency(summary.average_order_value),
        icon: '💰',
        color: 'indigo'
      }
    );
  }

  return (
    <Card className="p-6">
      <AppCardTitle className="text-gray-900 mb-4">ملخص مالي</AppCardTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <Card
            key={index}
            className={`bg-${metric.color}-50 border-${metric.color}-200 p-4`}
          >
            <div className="flex items-center">
              <span className="text-2xl ml-3">{metric.icon}</span>
              <div>
                <AppBodyText className="font-medium text-gray-600">{metric.label}</AppBodyText>
                <AppCardTitle className={`text-${metric.color}-900`}>
                  {metric.value}
                </AppCardTitle>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Card>
  );
};
